import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Button,
  Grid,
  Box,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import HideImageIcon from '@mui/icons-material/HideImage';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import PercentIcon from '@mui/icons-material/Percent';
import NumbersIcon from '@mui/icons-material/Numbers';
import TuneIcon from '@mui/icons-material/Tune';
import CallToActionIcon from '@mui/icons-material/CallToAction';
import CloseIcon from '@mui/icons-material/Close';
import type { Warehouse } from '../types/inventory.types';

interface BulkActionSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelectAction: (action: string) => void;
  selectedWarehouse?: Warehouse | null;
}

export const BulkActionSelectionModal: React.FC<BulkActionSelectionModalProps> = ({
  open,
  onClose,
  onSelectAction,
  selectedWarehouse,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          mx: { xs: 2, sm: 3 }
        }
      }}
    >
      <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2, px: 3 }}>
        <Box display="flex" alignItems="center">
          <CallToActionIcon sx={{ mr: 1, fontSize: 28 }} />
          <Typography variant="h6" fontWeight="bold">
            Toplu İşlem Seç
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Yapmak istediğiniz toplu işlemi seçin, ardından ürünleri işaretleyip işlemi gerçekleştirin.
        </Typography>
        <Grid container spacing={2}>
          {/* Toplu Sil */}
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              size="large"
              startIcon={<DeleteIcon />}
              onClick={() => onSelectAction('delete')}
            >
              Toplu Sil
            </Button>
          </Grid>
          
          {/* Toplu Pasife Al */}
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="outlined"
              color="warning"
              size="large"
              startIcon={<BlockIcon />}
              onClick={() => onSelectAction('inactivate')}
            >
              Toplu Pasife Al
            </Button>
          </Grid>
          
          {/* Toplu Satış Fiyatı Güncelle */}
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="outlined"
              color="success"
              size="large"
              startIcon={<PriceChangeIcon />}
              onClick={() => onSelectAction('price')}
            >
              Toplu Satış Fiyatı Güncelle
            </Button>
          </Grid>
          
          {/* Toplu Alış Fiyatı Güncelle */}
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="outlined"
              color="info"
              size="large"
              startIcon={<MonetizationOnIcon />}
              onClick={() => onSelectAction('purchasePrice')}
            >
              Toplu Alış Fiyatı Güncelle
            </Button>
          </Grid>
          
          {/* E-Ticarete Aç */}
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="outlined"
              color="success"
              size="large"
              startIcon={<StorefrontIcon />}
              onClick={() => onSelectAction('ecommerceOpen')}
            >
              E-Ticarete Aç
            </Button>
          </Grid>
          
          {/* E-Ticaretten Kapat */}
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              size="large"
              startIcon={<StorefrontIcon />}
              onClick={() => onSelectAction('ecommerceClose')}
            >
              E-Ticaretten Kapat
            </Button>
          </Grid>
          
          {/* QR Menü'de Aç */}
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              size="large"
              startIcon={<PhotoCameraIcon />}
              onClick={() => onSelectAction('qrmenu')}
            >
              QR Menü'de Aç
            </Button>
          </Grid>
          
          {/* Kiosk'ta Aç */}
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              size="large"
              startIcon={<PhotoCameraIcon />}
              onClick={() => onSelectAction('kiosk')}
            >
              Kiosk'ta Aç
            </Button>
          </Grid>
          
          {/* Toplu Resim Sil */}
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              size="large"
              startIcon={<HideImageIcon />}
              onClick={() => onSelectAction('deleteImages')}
            >
              Toplu Resim Sil
            </Button>
          </Grid>
          
          {/* Toplu Depo Ata */}
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              size="large"
              startIcon={<WarehouseIcon />}
              onClick={() => onSelectAction('assignWarehouse')}
            >
              Toplu Depo Ata
            </Button>
          </Grid>
          
          {/* Toplu KDV Güncelle */}
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              size="large"
              startIcon={<PercentIcon />}
              onClick={() => onSelectAction('vatRate')}
            >
              Toplu KDV Güncelle
            </Button>
          </Grid>
          
          {/* Toplu ÖKC Güncelle */}
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              size="large"
              startIcon={<NumbersIcon />}
              onClick={() => onSelectAction('departmentNo')}
            >
              Toplu ÖKC Güncelle
            </Button>
          </Grid>
          
          {/* Toplu KDV + ÖKC Güncelle */}
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              size="large"
              startIcon={<TuneIcon />}
              onClick={() => onSelectAction('vatAndDept')}
            >
              Toplu KDV + ÖKC Güncelle
            </Button>
          </Grid>
          
          {/* Tüm Stokları Sil */}
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              size="large"
              startIcon={<DeleteIcon />}
              onClick={() => onSelectAction('deleteAll')}
            >
              Tüm Stokları Sil {selectedWarehouse ? `(${selectedWarehouse.name})` : ''}
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

